/**
 * Data layer for the Marketplace section's insight dashboards.
 *
 * Today only the COMPANY panel has these pages, so the adapters live here and
 * container/B2B/CompanyAdmin/Marketplace/companyMarketplaceApi.js supplies the
 * company/v1 data source. Keeping the shaping here means a future super-admin
 * marketplace dashboard reads the payload exactly the same way.
 */

/** Money for a stat card: whole pounds, thousands separated. */
export const formatMoney = (value) => {
  if (value == null || isNaN(value)) return '-';
  return `£${Math.round(value).toLocaleString()}`;
};

/** Money inside a chart tooltip, where the pence still matter. */
export const formatMoneyExact = (value) => {
  if (value == null || isNaN(value)) return '-';
  return `£${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/** Shared shaping for the E-Gifts dashboard. */
export const adaptEGifts = (data) => ({
  totalOrders: data.totalOrders ?? 0,
  totalValue: data.totalValue ?? 0,
  redeemers: data.redeemers ?? 0,
  avgOrderValue: data.avgOrderValue ?? 0,
  successfulOrders: data.successfulOrders ?? 0,
  successRate: data.successRate ?? 0,
  giftedOrders: data.giftedOrders ?? 0,

  // Backend zero-fills the last 30 days; charts consume plain series.
  ordersPerDay: (data.ordersTrend || []).map((day) => day.orders ?? 0),
  valuePerDay: (data.ordersTrend || []).map((day) => day.value ?? 0),

  statusSplit: data.statusSplit || [],
  typeSplit: data.typeSplit || [],
  providerSplit: data.providerSplit || [],
  valueDistribution: data.valueDistribution || [],
  ordersPerUserDistribution: data.ordersPerUserDistribution || [],
  weekdayOrders: data.weekdayOrders || [],
  topBrands: (data.topBrands || []).map((brand) => ({
    id: brand._id,
    name: brand.name,
    orders: brand.orders,
    value: brand.value,
    buyers: brand.buyers
  }))
});

/**
 * Shared shaping for the user-detail page.
 *
 * As on the dashboards, the trend arrives as `[{ date, orders, value }]` and
 * the charts plot plain numbers — feeding the objects straight through renders
 * NaN paths and takes the page down, so the flattening lives here rather than
 * in each caller.
 */
export const adaptUserDetail = (data) => ({
  user: data.user,
  ordersPerDay: (data.ordersTrend || []).map((day) => day.orders ?? 0),
  valuePerDay: (data.ordersTrend || []).map((day) => day.value ?? 0),
  statusSplit: data.statusSplit || [],
  topBrands: (data.topBrands || []).map((brand) => ({
    id: brand._id,
    name: brand.name,
    orders: brand.orders,
    value: brand.value
  })),
  recentOrders: data.recentOrders || []
});

/** Shared shaping for the VIP offers dashboard. */
export const adaptVipOffers = (data) => ({
  // Catalogue half — Shoorah-wide content, identical for every company.
  totalOffers: data.totalOffers ?? 0,
  liveOffers: data.liveOffers ?? 0,
  draftOffers: data.draftOffers ?? 0,
  expiredOffers: data.expiredOffers ?? 0,
  specialCoupons: data.specialCoupons ?? 0,
  brands: data.brands ?? 0,
  avgDiscount: data.avgDiscount ?? 0,
  bestDiscount: data.bestDiscount ?? 0,

  // Engagement half — scoped to this company's people.
  totalClicks: data.totalClicks ?? 0,
  clickers: data.clickers ?? 0,
  throughPuts: data.throughPuts ?? 0,
  throughPutRate: data.throughPutRate ?? 0,

  clicksPerDay: (data.clicksTrend || []).map((day) => day.clicks ?? 0),
  clickTypeSplit: data.clickTypeSplit || [],
  discountDistribution: data.discountDistribution || [],
  expiryDistribution: data.expiryDistribution || [],
  categorySplit: data.categorySplit || [],
  topOffers: (data.topOffers || []).map((offer) => ({
    id: offer._id,
    name: offer.name,
    clicks: offer.clicks,
    discount: offer.discount ?? null,
    isExpired: Boolean(offer.isExpired)
  }))
});
